import apiService from './apiService';
import authService from './authService';

class ReportService {
  buildTripReportParams(filters = {}, { includePort = true } = {}) {
    const params = new URLSearchParams();

    if (includePort && filters.port_ids) {
      if (Array.isArray(filters.port_ids)) {
        filters.port_ids.forEach((id) => params.append('port_ids', id));
      } else {
        params.append('port_ids', filters.port_ids);
      }
    }

    if (filters.transporter_ids) {
      if (Array.isArray(filters.transporter_ids)) {
        filters.transporter_ids.forEach((id) => params.append('transporter_ids', id));
      } else {
        params.append('transporter_ids', filters.transporter_ids);
      }
    }

    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    if (filters.trip_status) {
      if (Array.isArray(filters.trip_status)) {
        filters.trip_status.forEach((status) => params.append('trip_status', status));
      } else {
        params.append('trip_status', filters.trip_status);
      }
    }

    if (filters.driver_ids) {
      if (Array.isArray(filters.driver_ids)) {
        filters.driver_ids.forEach((id) => params.append('driver_ids', id));
      } else {
        params.append('driver_ids', filters.driver_ids);
      }
    }

    if (filters.vehicle_ids) {
      if (Array.isArray(filters.vehicle_ids)) {
        filters.vehicle_ids.forEach((id) => params.append('vehicle_ids', id));
      } else {
        params.append('vehicle_ids', filters.vehicle_ids);
      }
    }

    if (filters.skip !== undefined) params.append('skip', filters.skip);
    else params.append('skip', 0);

    if (filters.limit !== undefined) params.append('limit', filters.limit);
    else params.append('limit', 100);

    if (filters.include_summary !== undefined) {
      params.append('include_summary', filters.include_summary);
    } else {
      params.append('include_summary', true);
    }

    return params;
  }

  async getTripReports(filters = {}) {
    try {
      const endpoint = `/api/v1/reports/trips/view?${this.buildTripReportParams(filters).toString()}`;
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Failed to fetch trip reports:', error);
      throw error;
    }
  }

  async getSecondaryTripReports(filters = {}) {
    try {
      const endpoint = `/api/v1/reports/trips/view/secondary?${this.buildTripReportParams(filters, { includePort: false }).toString()}`;
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Failed to fetch secondary trip reports:', error);
      throw error;
    }
  }

  async exportTripReport(exportData) {
    try {
      const endpoint = `/api/v1/reports/trips/view/export`;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.geonexs.com'}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader(),
        },
        body: JSON.stringify(exportData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to export trip report:', error);
      throw error;
    }
  }

  async exportTripReportExcel(filters = {}) {
    try {
      const endpoint = `/api/v1/reports/trips/view/excel?${this.buildTripReportParams(filters).toString()}`;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.geonexs.com'}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ...authService.getAuthHeader(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to export trip report to Excel:', error);
      throw error;
    }
  }

  async exportSecondaryTripReportExcel(filters = {}) {
    try {
      const params = this.buildTripReportParams(filters, { includePort: false });
      params.delete('include_summary');
      params.delete('skip');
      params.delete('limit');

      const endpoint = `/api/v1/reports/trips/view/secondary/excel?${params.toString()}`;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.geonexs.com'}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ...authService.getAuthHeader(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to export secondary trip report to Excel:', error);
      throw error;
    }
  }



  async getTripReportSummary(filters = {}) {
    try {
      const params = new URLSearchParams();
    
      if (filters.port_ids) {
        if (Array.isArray(filters.port_ids)) {
          filters.port_ids.forEach(id => params.append('port_ids', id));
        } else {
          params.append('port_ids', filters.port_ids);
        }
      }
      
      if (filters.transporter_ids) {
        if (Array.isArray(filters.transporter_ids)) {
          filters.transporter_ids.forEach(id => params.append('transporter_ids', id));
        } else {
          params.append('transporter_ids', filters.transporter_ids);
        }
      }
      
   
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      
      if (filters.trip_status) {
        if (Array.isArray(filters.trip_status)) {
          filters.trip_status.forEach(status => params.append('trip_status', status));
        } else {
          params.append('trip_status', filters.trip_status);
        }
      }
      
      if (filters.driver_ids) {
        if (Array.isArray(filters.driver_ids)) {
          filters.driver_ids.forEach(id => params.append('driver_ids', id));
        } else {
          params.append('driver_ids', filters.driver_ids);
        }
      }
      
      if (filters.vehicle_ids) {
        if (Array.isArray(filters.vehicle_ids)) {
          filters.vehicle_ids.forEach(id => params.append('vehicle_ids', id));
        } else {
          params.append('vehicle_ids', filters.vehicle_ids);
        }
      }

      const endpoint = `/api/v1/reports/trips/view/summary?${params.toString()}`;
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch trip report summary:', error);
      throw error;
    }
  }

  async getReportFilterOptions() {
    try {
  
      const endpoint = '/api/v1/reports/trips/view/filters';
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch report filter options:', error);
      throw error;
    }
  }
  
  async getRakeMovementReports(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.rake_id) params.append('rake_id', filters.rake_id);
      if (filters.product_type_id) params.append('product_type_id', filters.product_type_id);

      params.append('skip', filters.skip !== undefined ? filters.skip : 0);
      params.append('limit', filters.limit !== undefined ? filters.limit : 100);

      const endpoint = `/api/v1/reports/rake-movements?${params.toString()}`;
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Failed to fetch rake movement reports:', error);
      throw error;
    }
  }

  async exportRakeMovementReportExcel(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.rake_id) params.append('rake_id', filters.rake_id);
      if (filters.product_type_id) params.append('product_type_id', filters.product_type_id);

      const endpoint = `/api/v1/reports/rake-movements/excel?${params.toString()}`;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.geonexs.com'}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ...authService.getAuthHeader(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to export rake movement report to Excel:', error);
      throw error;
    }
  }

  formatRakeMovementReportData(reportData) {
    return reportData.map((row) => ({
      rake_movement_id: row.rake_movement_id || 'N/A',
      trip_number: row.trip_number || 'N/A',
      port_name: row.port_name || 'N/A',
      date: row.date || 'N/A',
      destination_state: row.destination_state || 'N/A',
      destination_district: row.destination_district || 'N/A',
      rake_point_name: row.rake_point_name || 'N/A',
      product_type_name: row.product_type_name || 'N/A',
      baseline_qty_mt: row.baseline_qty_mt !== undefined && row.baseline_qty_mt !== null ? row.baseline_qty_mt : 'N/A',
      receipt_qty: row.receipt_qty !== undefined && row.receipt_qty !== null ? row.receipt_qty : 'N/A',
      missing_wagons: row.missing_wagons !== undefined && row.missing_wagons !== null ? row.missing_wagons : 'N/A',
      variance_qty_mt: row.variance_qty_mt !== undefined && row.variance_qty_mt !== null ? row.variance_qty_mt : 'N/A',
      variance_type: row.variance_type || 'N/A',
    }));
  }

  formatSecondaryTripReportData(tripData) {
    return this.formatTripReportData(tripData).map((trip, index) => ({
      ...trip,
      parent_trip_number: tripData[index]?.parent_trip_number || 'N/A',
      origin_rake_point_name: tripData[index]?.origin_rake_point_name || 'N/A',
      billed_to_party: tripData[index]?.billed_to_party || 'N/A',
      shipped_to_party: tripData[index]?.shipped_to_party || 'N/A',
      movement: tripData[index]?.movement || 'Secondary',
    }));
  }

  formatTripReportData(tripData) {
    return tripData.map(trip => ({
      trip_id: trip.trip_id || 'N/A',
      trip_number: trip.trip_number || 'N/A',
      port_name: trip.port_name || trip.start_port?.name || 'N/A',
      port_id: trip.port_id || 'N/A',
      scheduled_start_time: trip.scheduled_start_time || 'N/A',
      actual_start_time: trip.actual_start_time || 'N/A',
      actual_end_time: trip.actual_end_time || 'N/A',
      start_location: trip.start_location || 'N/A',
      end_location: trip.end_location || 'N/A',
      product_type_name: trip.product_type_name || trip.product?.name || 'N/A',
      product_quantity: trip.product_quantity || 'N/A',
      vehicle_number: trip.vehicle_number || trip.vehicle?.truck_number || 'N/A',
      username: trip.username || trip.driver?.name || 'N/A',
      phone_number: trip.phone_number || trip.driver?.mobile || 'N/A',
      consignee_name: trip.consignee_name || 'N/A',
      consignee_address: trip.consignee_address || 'N/A',
      consignee_phone: trip.consignee_phone || 'N/A',
      status: trip.status || 'N/A',
      calculated_start_location: trip.calculated_start_location || 'N/A',
      calculated_start_timestamp: trip.calculated_start_timestamp || 'N/A',
      calculated_end_location: trip.calculated_end_location || 'N/A',
      calculated_end_timestamp: trip.calculated_end_timestamp || 'N/A',
      trip_item_id: trip.trip_item_id || 'N/A',
      eway_bill_number: trip.eway_bill_number || 'N/A',
      eway_bill_date: trip.eway_bill_date || 'N/A',
      distributor_name: trip.distributor_name || 'N/A',
      data_source: trip.data_source || 'N/A',
      transporter_id: trip.transporter_id || 'N/A',
      driver_id: trip.driver_id || 'N/A',
      vehicle_id: trip.vehicle_id || 'N/A',
      created_at: trip.created_at || 'N/A',
      created_by: trip.created_by || 'N/A',
      trip_distributor_id: trip.trip_distributor_id || 'N/A',
      item_distributor_id: trip.item_distributor_id || 'N/A',
      distributor_district_name: trip.distributor_district_name || 'N/A',
      distributor_state_name: trip.distributor_state_name || 'N/A',
    }));
  }
}

export default new ReportService();